import React from 'react';

import { useTranslation } from '@shared/core';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import { Box, ButtonV2, SpacingStack, TextV2 } from '@withjoy/joykit';
import { ShippingAddressOptions } from '@apps/registry/common/components/ShoppingCart';
import { CartFooterSubmitButton, ShippingAddressAtom, WarningAddressAtom } from '@apps/registry/common/components/ShoppingCart/components';

import { AddressForm } from './AddressForm';
import type { AddressFormFields } from './types';
import type { AddressUsedEnum } from '@shared/components/AddressAutocompleteField/constants';
import { StandardShippingOption } from '../../../components/AddressAtoms/StandardShipping';
import type { FormikProps } from 'formik';
import type { ShippingAddressFragment } from '@graphql/fragments/shippingAddress.graphql.generated';
import type { ShippingFormFields } from './ShippingStep.controller';
import { interpretShippingDecimalString } from '@apps/registry/common/utils/shippingAddress';

export interface ShippingStepProps {
  isFormOpen: boolean;
  setIsFormOpen: (value: boolean) => void;
  formFields: ShippingFormFields;
  formik: FormikProps<AddressFormFields>;
  coupleAddress: Maybe<ShippingAddressFragment>;
  supportsShipToMe: boolean;
  isLowInventory: boolean;
  shippingFee: Maybe<string>;
  shippingOptionSelected: ShippingAddressOptions;
  setShippingOptionSelected: (value: ShippingAddressOptions) => void;
  setSuggestedAddress: (address: AddressFormFields | null) => void;
  setAddressUsed: (addressUsed: AddressUsedEnum) => void;
  updateShippingOption: (option: ShippingAddressOptions) => void;
  shippingAddressFormValues: AddressFormFields;
  customShippingAddress?: ShippingAddressFragment | null;
  saveAddress: () => Promise<unknown>;
  disableNextStep?: boolean;
}

export const ShippingStep: React.FC<ShippingStepProps> = ({
  disableNextStep,
  isFormOpen,
  setIsFormOpen,
  formFields,
  formik,
  coupleAddress,
  supportsShipToMe,
  isLowInventory,
  shippingFee,
  shippingOptionSelected,
  setShippingOptionSelected,
  setSuggestedAddress,
  setAddressUsed,
  updateShippingOption,
  shippingAddressFormValues,
  customShippingAddress,
  saveAddress,
}) => {
  const isMobileOrTablet = useIsMobileOrTablet();

  const { t2, tq } = useTranslation('sharedRegistry');
  const {
    shippingMethod: shippingMethodLabel,
    freeShipping,
    shipTo,
    giftEmoji,
    onlyUSAddress,
    shipToMe,
    weWillTellAboutPurchase,
    standardShipping,
  } = t2('shoppingCart', 'shippingAddressStep');
  const { editButton } = t2('shoppingCart', 'shippingAddressStep', 'addressForm');
  const shippingFeeInterpretation = interpretShippingDecimalString(shippingFee);

  const createAddressLabel = (address: string) => {
    return `${giftEmoji} ${address}`;
  };

  return (
    <SpacingStack
      spacing={6}
      width="100%"
    >
      <div>
        <TextV2
          typographyVariant="label3"
          marginBottom={3}
        >
          {shippingMethodLabel}
        </TextV2>
        <StandardShippingOption
          label={shippingFeeInterpretation.isFreeShipping ? freeShipping : standardShipping}
          value={shippingFeeInterpretation.floatingPointCurrencyString}
        />
      </div>
      <div>
        <TextV2
          typographyVariant="label3"
          marginBottom={3}
        >
          {shipTo}
        </TextV2>
        <SpacingStack
          spacing={3}
          width="100%"
        >
          {!coupleAddress?.name ? (
            <>
              <WarningAddressAtom />
            </>
          ) : (
            <ShippingAddressAtom
              isSelected={shippingOptionSelected === ShippingAddressOptions.CoupleAddress}
              label={createAddressLabel(coupleAddress.name)}
              note={`${coupleAddress.city}, ${coupleAddress.state}, ${coupleAddress.postalCode}`}
              onSelect={() => {
                updateShippingOption(ShippingAddressOptions.CoupleAddress);
                setShippingOptionSelected(ShippingAddressOptions.CoupleAddress);
                setIsFormOpen(false);
              }}
            />
          )}
          {supportsShipToMe ? (
            <ShippingAddressAtom
              isSelected={shippingOptionSelected === ShippingAddressOptions.ShipToMe}
              label={createAddressLabel(shipToMe)}
              note={weWillTellAboutPurchase}
              tooltip={onlyUSAddress}
              onSelect={() => {
                setShippingOptionSelected(ShippingAddressOptions.ShipToMe);
                if (customShippingAddress?.name) {
                  updateShippingOption(ShippingAddressOptions.ShipToMe);
                } else {
                  setIsFormOpen(true);
                }
              }}
              address={shippingAddressFormValues?.name ? shippingAddressFormValues : undefined}
            />
          ) : (
            isLowInventory &&
            !!coupleAddress?.name && (
              <TextV2
                typographyVariant="label2"
                color="mono12"
              >
                {tq`One or more items in your cart have limited stock and can only ship to the couple's address.`}
              </TextV2>
            )
          )}
        </SpacingStack>
        {isFormOpen ? (
          <Box marginTop={6}>
            <AddressForm
              handleCancelClick={() => {
                setIsFormOpen(false);
                setShippingOptionSelected(ShippingAddressOptions.CoupleAddress);
              }}
              handleSaveClick={() => {
                setIsFormOpen(false);
                saveAddress();
              }}
              formFields={formFields}
              setSuggestedAddress={setSuggestedAddress}
              setAddressUsed={setAddressUsed}
              formik={formik}
            />
          </Box>
        ) : (
          !!shippingAddressFormValues?.name && (
            <ButtonV2
              marginTop={6}
              variant="muted"
              onClick={() => setIsFormOpen(true)}
              fullWidth
            >
              {editButton}
            </ButtonV2>
          )
        )}
        {!isMobileOrTablet && !isFormOpen && <CartFooterSubmitButton disabled={disableNextStep} />}
      </div>
    </SpacingStack>
  );
};
